import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Edit, Eye } from "react-feather";
import Table from "../../pagination/datatable";
import TooltipIcons from "../../common/tooltip-content/tooltipIcons";
import RefreshIcon from "../../common/tooltip-content/refresh";
import CollapesIcon from "../../common/tooltip-content/collapes";
import { PlusCircle } from "feather-icons-react/build/IconComponents";
import FranchiseServices from "../../../services/FranchiseServices";
import BrandService from "../../../services/BrandService";
import useAsync from "../../../hooks/useAsync";
import UserService from "../../../services/UserServices";

const FranchiseList = () => {
  const { data: users } = useAsync(() => {
    return UserService.getAllUsers();
  }, []);

  const { data: brands, loading: brandsLoading } = useAsync(() => {
    return BrandService.getAllBrands();
  }, []);

  const { data: franchises, loading: franchisesLoading } = useAsync(() => {
    return FranchiseServices.getBrandFranchises(brands[0]?._id);
  }, [brands, brandsLoading]);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    brandId: "",
    franchiseOwner: "",
    status: "active",
  });

  const [editFormData, setEditFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    brandId: "",
    franchiseOwner: "",
    status: "",
  });

  const [editingFranchiseId, setEditingFranchiseId] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const franchiseData = {
        name: formData.name,
        franchiseOwner: formData.franchiseOwner,
        status: formData.status,
        contact: {
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
        },
      };

      await FranchiseServices.createFranchise(formData.brandId, franchiseData);
      setFormData({
        name: "",
        email: "",
        phone: "",
        address: "",
        brandId: "",
        franchiseOwner: "",
        status: "active",
      });
      window.location.reload();
    } catch (error) {
      console.error("Error creating franchise:", error);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const franchiseData = {
        name: editFormData.name,
        franchiseOwner: editFormData.franchiseOwner,
        status: editFormData.status,
        contact: {
          email: editFormData.email,
          phone: editFormData.phone,
          address: editFormData.address,
        },
      };

      const franchise = franchises.find((f) => f._id === editingFranchiseId);
      const brandId = editFormData.brandId || franchise?.brand?._id;

      if (!brandId) {
        console.error("No brand found for franchise");
        return;
      }

      await FranchiseServices.updateFranchise(
        brandId,
        editingFranchiseId,
        franchiseData
      );
      setEditingFranchiseId(null);
      setEditFormData({
        name: "",
        email: "",
        phone: "",
        address: "",
        brandId: "",
        franchiseOwner: "",
        status: "",
      });
      window.location.reload();
    } catch (error) {
      console.error("Error updating franchise:", error);
    }
  };

  const handleDelete = async () => {
    try {
      // Get the brand from the franchise to be deleted
      const franchise = franchises.find((f) => f._id === editingFranchiseId);
      const brandId = franchise?.brand?._id;
      if (!brandId) {
        console.error("No brand found for franchise");
        return;
      }

      await FranchiseServices.deleteFranchise(brandId, editingFranchiseId);
      setEditingFranchiseId(null);
      window.location.reload();
    } catch (error) {
      console.error("Error deleting franchise:", error);
    }
  };

  const columns = [
    {
      title: "Brand Name",
      dataIndex: ["brand", "name"],
      sorter: (a, b) =>
        (a.brand?.name || "").length - (b.brand?.name || "").length,
    },
    {
      title: "Franchise Name",
      dataIndex: "name",
      sorter: (a, b) => a.name.length - b.name.length,
    },
    {
      title: "Email",
      dataIndex: ["contact", "email"],
      sorter: (a, b) => a.contact.email.length - b.contact.email.length,
    },
    {
      title: "Phone",
      dataIndex: ["contact", "phone"],
      sorter: (a, b) => a.contact.phone.length - b.contact.phone.length,
    },
    {
      title: "Address",
      dataIndex: ["contact", "address"],
      sorter: (a, b) => a.contact.address.length - b.contact.address.length,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <span
          className={`badge badge-${
            status === "active" ? "success" : "danger"
          } d-inline-flex align-items-center badge-xs`}
        >
          <i className="ti ti-point-filled me-1" />
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      ),
      sorter: (a, b) => a.status.length - b.status.length,
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            <div className="input-block add-lists"></div>

            <Link
              className="me-2 p-2"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#view-franchise"
              onClick={() => {
                setEditingFranchiseId(record._id);
                setEditFormData({
                  name: record.name,
                  email: record.contact.email,
                  phone: record.contact.phone,
                  address: record.contact.address,
                });
              }}
            >
              <Eye className="feather-view" />
            </Link>

            <Link
              className="me-2 p-2"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#edit-franchise"
              onClick={() => {
                setEditingFranchiseId(record._id);
                setEditFormData({
                  name: record.name,
                  email: record.contact.email,
                  phone: record.contact.phone,
                  address: record.contact.address,
                });
              }}
            >
              <Edit className="feather-edit" />
            </Link>

            <Link
              data-bs-toggle="modal"
              data-bs-target="#delete-modal"
              className="p-2 d-flex align-items-center border rounded"
              to="#"
              onClick={() => setEditingFranchiseId(record._id)}
            >
              <i data-feather="trash-2" className="feather-trash-2" />
            </Link>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Franchise</h4>
                <h6>Manage your Franchise</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <TooltipIcons />
              <RefreshIcon />
              <CollapesIcon />
            </ul>
            <div className="page-btn">
              <Link
                to="#"
                data-bs-toggle="modal"
                data-bs-target="#add-franchise"
                className="btn btn-primary"
              >
                <PlusCircle data-feather="plus-circle" className=" me-2" />
                Add Franchise
              </Link>
            </div>
          </div>

          {/* /product list */}
          <div className="card table-list-card">
            <div className="card-body">
              <div className="table-responsive">
                {!franchisesLoading && (
                  <Table columns={columns} dataSource={franchises} />
                )}
              </div>
            </div>
          </div>

          {/* /product list */}
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2014-2025 © Style Buzz Pro. All Right Reserved</p>
          <p>
            Designed &amp; Developed By{" "}
            <Link to="#" className="text-primary">
              Style Buzz Pro
            </Link>
          </p>
        </div>
      </div>
      <div className="modal fade" id="add-franchise">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <div className="page-title">
                <h4>Add Franchise</h4>
              </div>
              <button
                type="button"
                className="close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">
                    Brand <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="brandId"
                    value={formData.brandId}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Brand</option>
                    {brands?.map((brand) => (
                      <option key={brand._id} value={brand._id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Franchise Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Franchise Owner <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="franchiseOwner"
                    value={formData.franchiseOwner}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Owner</option>
                    {users?.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Phone <span className="text-danger">*</span>
                  </label>
                  <input
                    type="tel"
                    className="form-control"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Address <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Status <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary fs-13 fw-medium p-2 px-3"
                >
                  Add Franchise
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Edit Franchise Modal */}
      <div className="modal fade" id="edit-franchise">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <div className="page-title">
                <h4>Edit Franchise</h4>
              </div>
              <button
                type="button"
                className="close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <form onSubmit={handleEdit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">
                    Brand <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="brandId"
                    value={editFormData.brandId}
                    onChange={handleEditChange}
                    required
                  >
                    <option value="">Select Brand</option>
                    {brands?.map((brand) => (
                      <option key={brand._id} value={brand._id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Franchise Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={editFormData.name}
                    onChange={handleEditChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Franchise Owner <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="franchiseOwner"
                    value={editFormData.franchiseOwner}
                    onChange={handleEditChange}
                    required
                  >
                    <option value="">Select Owner</option>
                    {users?.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={editFormData.email}
                    onChange={handleEditChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Phone <span className="text-danger">*</span>
                  </label>
                  <input
                    type="tel"
                    className="form-control"
                    name="phone"
                    value={editFormData.phone}
                    onChange={handleEditChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Address <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    name="address"
                    value={editFormData.address}
                    onChange={handleEditChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Status <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    name="status"
                    value={editFormData.status}
                    onChange={handleEditChange}
                    required
                  >
                    <option value="">Select Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary fs-13 fw-medium p-2 px-3"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* View Franchise Modal */}
      <div className="modal fade" id="view-franchise">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <div className="page-title">
                <h4>Franchise Details</h4>
              </div>
              <button
                type="button"
                className="close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div className="modal-body">
              <div className="info-details">
                <div className="details-list">
                  <div className="customer-details">
                    <span>Franchise Name:</span>
                    <h6>{editFormData.name}</h6>
                  </div>
                  <div className="customer-details">
                    <span>Franchise Owner:</span>
                    <h6>{editFormData.franchiseOwner}</h6>
                  </div>
                  <div className="customer-details">
                    <span>Email:</span>
                    <h6>{editFormData.email}</h6>
                  </div>
                  <div className="customer-details">
                    <span>Phone:</span>
                    <h6>{editFormData.phone}</h6>
                  </div>
                  <div className="customer-details">
                    <span>Address:</span>
                    <h6>{editFormData.address}</h6>
                  </div>
                  <div className="customer-details">
                    <span>Status:</span>
                    <h6>{editFormData.status}</h6>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <div className="modal fade" id="delete-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content p-5 px-3 text-center">
                <span className="rounded-circle d-inline-flex p-2 bg-danger-transparent mb-2">
                  <i className="ti ti-trash fs-24 text-danger" />
                </span>
                <h4 className="fs-20 text-gray-9 fw-bold mb-2 mt-1">
                  Delete Franchise
                </h4>
                <p className="text-gray-6 mb-0 fs-16">
                  Are you sure you want to delete this franchise?
                </p>
                <div className="modal-footer-btn mt-3 d-flex justify-content-center">
                  <button
                    type="button"
                    className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    data-bs-dismiss="modal"
                    className="btn btn-submit fs-13 fw-medium p-2 px-3"
                    onClick={handleDelete}
                  >
                    Yes Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FranchiseList;
